import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  ButtonBase,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GpsOffIcon from '@mui/icons-material/GpsOff';
import { KPI_COLORS, KPI_KEYS } from '../constants';
import { getMetricCount } from '../mapMetrics';

const KPI_CONFIG = [
  { key: KPI_KEYS.TOTAL, label: 'Total', icon: <FormatListBulletedIcon /> },
  { key: KPI_KEYS.ANOMALIAS, label: 'Anomalias', icon: <ReportProblemIcon />, pulse: true },
  { key: KPI_KEYS.AGUARDANDO, label: 'Aguardando', icon: <HourglassEmptyIcon /> },
  { key: KPI_KEYS.EM_INSPECAO, label: 'Em inspeção', icon: <SearchIcon /> },
  { key: KPI_KEYS.FINALIZADAS, label: 'Finalizadas', icon: <CheckCircleOutlineIcon /> },
  { key: KPI_KEYS.GPS_ERRORS, label: 'Erros GPS', icon: <GpsOffIcon /> },
];

const ROW_DIVIDER_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'stretch',
  '& > *': {
    flex: 1,
    minWidth: { xs: '50%', sm: 140, md: 0 },
  },
  '& > *:not(:first-of-type)': {
    borderLeft: { sm: 1 },
    borderColor: 'divider',
  },
};

const resolveColor = (theme, colorKey) => {
  if (!colorKey || colorKey === 'default' || !theme.palette[colorKey]) {
    return theme.palette.text.secondary;
  }
  return theme.palette[colorKey].main;
};

const formatNumber = (n) => (n ?? 0).toLocaleString('pt-BR');

const KpiCell = ({ config, count, active, onClick }) => {
  const colorKey = KPI_COLORS[config.key] || 'default';

  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      sx={{
        width: '100%',
        justifyContent: 'flex-start',
        textAlign: 'left',
        px: 2,
        py: 1.75,
        borderRadius: 0,
        transition: 'background-color 0.2s ease',
        backgroundColor: (theme) =>
          active ? `${resolveColor(theme, colorKey)}14` : 'transparent',
        '&:hover': {
          backgroundColor: (theme) => `${resolveColor(theme, colorKey)}0F`,
        },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%' }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: (theme) => resolveColor(theme, colorKey),
            backgroundColor: (theme) => `${resolveColor(theme, colorKey)}1F`,
            border: (theme) =>
              active ? `2px solid ${resolveColor(theme, colorKey)}` : '2px solid transparent',
            ...(config.pulse && count > 0 && {
              animation: 'pulse-red 2s infinite',
              '@keyframes pulse-red': {
                '0%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0.35)' },
                '70%': { boxShadow: '0 0 0 8px rgba(244, 67, 54, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(244, 67, 54, 0)' },
              },
            }),
          }}
        >
          {React.cloneElement(config.icon, { sx: { fontSize: 20 } })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.8125rem' }}
            noWrap
            title={config.label}
          >
            {config.label}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              lineHeight: 1.2,
              color: config.key === KPI_KEYS.ANOMALIAS && count > 0 ? 'error.main' : 'text.primary',
            }}
          >
            {formatNumber(count)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            NSIRs
          </Typography>
        </Box>
      </Stack>
    </ButtonBase>
  );
};

KpiCell.propTypes = {
  config: PropTypes.object.isRequired,
  count: PropTypes.number.isRequired,
  active: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
};

KpiCell.defaultProps = {
  active: false,
};

const KpiSkeleton = () => (
  <Box sx={{ px: 2, py: 1.75 }}>
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width="70%" height={18} />
        <Skeleton width="40%" height={28} />
      </Box>
    </Stack>
  </Box>
);

const NsirMapaKpiStrip = ({ metrics, loading, activeKpi, onSelectKpi }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
    <Box sx={ROW_DIVIDER_SX}>
      {loading
        ? KPI_CONFIG.map((config) => <KpiSkeleton key={config.key} />)
        : KPI_CONFIG.map((config) => {
            const isTotal = config.key === KPI_KEYS.TOTAL;
            const active = isTotal
              ? activeKpi == null
              : activeKpi === config.key;

            return (
              <KpiCell
                key={config.key}
                config={config}
                count={getMetricCount(metrics, config.key)}
                active={active}
                onClick={() => {
                  if (config.key === KPI_KEYS.GPS_ERRORS) {
                    onSelectKpi(config.key);
                    return;
                  }
                  if (isTotal) {
                    onSelectKpi(null);
                  } else {
                    onSelectKpi(activeKpi === config.key ? null : config.key);
                  }
                }}
              />
            );
          })}
    </Box>
  </Paper>
);

NsirMapaKpiStrip.propTypes = {
  metrics: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  activeKpi: PropTypes.string,
  onSelectKpi: PropTypes.func.isRequired,
};

NsirMapaKpiStrip.defaultProps = {
  loading: false,
  activeKpi: null,
};

export default NsirMapaKpiStrip;
